import {
  ConsultaDeCertificadosSchema,
  FILTROS_DE_COLUMNA_CERTIFICADOS_ANALISIS,
  FILTROS_DE_COLUMNA_CERTIFICADOS_ORIGEN,
  TIPOS_DE_CARGA_DE_CERTIFICADOS,
  type AlcanceDeEliminarCertificados,
  type ConsultaDeCertificados,
  type Comercializador,
  type FechasDelAnalisis,
  type GrupoDeRecoleccion,
  type PerfilDePlanta,
  type PerfilDeTitulo,
  type ResultadoDeCargaDeCertificados,
  type ResultadoDeCertificadosDeRecoleccion,
  type TipoDeCargaDeCertificados,
  type TituloParaCertificados,
} from '@cnc/contracts';
import {useQueryClient} from '@tanstack/react-query';
import {useEffect, useState} from 'react';
import {useSearchParams} from 'react-router';
import {ErrorDeApi} from '../api/cliente';
import {desdeParametros} from '../api/consulta';
import {
  cargarAnalisisPorLotes,
  certificadosDeColombianMint,
  parametrosDeCertificados,
  useOpcionesDeColumnaDeCertificados,
  RUTA_RELACION_POR_BIG_BAG,
  rutaDeDocumentoDeAnalisis,
  rutaDeDocumentoDeOrigen,
  useCargarCertificados,
  useCrearCertificadosDeRecoleccion,
  useEliminarCertificados,
  useRegistrarCertificadoPorFuera,
  usePaqueteDeCertificados,
  rutaDelPaqueteDeCertificados,
  usePropuestaDeRecoleccion,
  useLeerFacturasDeExpedientes,
  useGuardarComercializador,
  useGuardarPerfilDePlanta,
  useGuardarPerfilDeTitulo,
  useListaDeCertificados,
  usePanelDeCertificados,
} from '../api/certificados';
import {CertificadosDeUnaExportacion} from '../componentes/certificados-de-una-exportacion';
import {AYUDA_CERTIFICADOS as A} from '../componentes/ayuda-exportaciones';
import {BotonDeEliminar} from '../componentes/boton-de-eliminar';
import {FiltrosPorColumna} from '../componentes/filtros-por-columna';
import type {ColumnaConFiltro, UseOpcionesDeColumna} from '../utilidades/columnas-con-filtro';
import {NotaDeAyuda} from '../componentes/nota-de-ayuda';
import {PistaDeSoltar, ZonaParaSoltar} from '../componentes/zona-para-soltar';
import {PestanasSeguimiento} from '../componentes/pestanas-seguimiento';
import {usePermisos} from '../componentes/sesion-contexto';
import {tr, trx, txt} from '../i18n/idioma';
import {fecha, numero, textoOGuion} from '../utilidades/formato';

const campo = 'mt-0.5 w-full rounded-md border border-linea bg-white px-2 py-1 text-sm text-tinta';
const boton =
  'rounded-md bg-verde px-3 py-1.5 text-sm font-medium text-white hover:bg-verde-oscuro disabled:opacity-50';
const botonSuave = 'rounded-md border border-linea bg-white px-3 py-1.5 text-sm hover:bg-papel disabled:opacity-50';

const NOMBRE_DE_CARGA: Record<TipoDeCargaDeCertificados, ReturnType<typeof txt>> = {
  generador: txt('Generador de Certificados de Origen', 'Origin certificate generator'),
  relacion: txt('Relación Pago Regalías', 'Royalty payment relation'),
  control: txt('Controles de empaque de las exportaciones (hoja TD)', 'Export packing controls (TD sheet)'),
  facturas: txt('Facturas de exportación (PDF)', 'Export invoices (PDF)'),
  analisis: txt(
    'Certificados de análisis de Colombian Mint (carpeta Recolecciones)',
    'Colombian Mint analysis certificates (Recolecciones folder)',
  ),
};

// Filtros de la lista, uno por columna (decision 0076).
const COLUMNAS_FILTRABLES_DE_ORIGEN: ColumnaConFiltro[] = (
  [
    ['titulo', txt('Título', 'Title')],
    ['numero', txt('No.', 'No.')],
    ['recoleccion', txt('Recolección', 'Pickup')],
    ['planta', txt('Planta', 'Plant')],
    ['auG', txt('Au (g)', 'Au (g)')],
    ['agG', txt('Ag (g)', 'Ag (g)')],
    ['fecha', txt('Fecha', 'Date')],
    ['mes', txt('Mes', 'Month')],
    ['analisis', txt('Análisis', 'Analysis')],
    ['bigBags', txt('Big Bags', 'Big Bags')],
    ['exportacion', txt('Exportación', 'Export')],
    ['fuente', txt('De dónde vino', 'Source')],
  ] as const
).map(([id, titulo]) => ({id, titulo, tipo: FILTROS_DE_COLUMNA_CERTIFICADOS_ORIGEN[id]}));
const COLUMNAS_FILTRABLES_DE_ANALISIS: ColumnaConFiltro[] = (
  [
    ['codigo', txt('No.', 'No.')],
    ['serie', txt('Serie', 'Series')],
    ['titulo', txt('Título', 'Title')],
    ['recoleccion', txt('Recolección', 'Pickup')],
    ['planta', txt('Planta', 'Plant')],
    ['origen', txt('C.O.', 'C.O.')],
    ['tonSecas', txt('Ton secas', 'Dry tons')],
    ['auG', txt('Au (g)', 'Au (g)')],
    ['agG', txt('Ag (g)', 'Ag (g)')],
    ['fecha', txt('Fecha', 'Date')],
    ['mes', txt('Mes', 'Month')],
    ['bigBags', txt('Big Bags', 'Big Bags')],
    ['exportacion', txt('Exportación', 'Export')],
    ['fuente', txt('De dónde vino', 'Source')],
  ] as const
).map(([id, titulo]) => ({id, titulo, tipo: FILTROS_DE_COLUMNA_CERTIFICADOS_ANALISIS[id]}));

const CAMPOS_DE_TITULO: Array<[keyof PerfilDeTitulo, ReturnType<typeof txt>]> = [
  ['explotador', txt('Explotador (razón social)', 'Operator (company name)')],
  ['documento', txt('NIT del explotador', 'Operator tax ID')],
  ['departamento', txt('Departamento', 'Department')],
  ['municipio', txt('Municipio', 'Municipality')],
  ['comprador', txt('Comprador', 'Buyer')],
  ['compradorDocumento', txt('Documento del comprador', 'Buyer ID')],
  ['compradorRucom', txt('RUCOM del comprador', 'Buyer RUCOM')],
  ['codigoArchivo', txt('Código del archivo (BV, MM, MV)', 'File code (BV, MM, MV)')],
  ['serieDeAnalisis', txt('Serie de los análisis (BV, MM, MV, MS)', 'Analysis series (BV, MM, MV, MS)')],
];

// Exportaciones → Certificados (decision 0067): consecutivos por titulo y por
// serie de analisis, los datos que imprimen los certificados, las cargas y la
// relacion de certificados por Big Bag.
export function PaginaCertificados() {
  const permisos = usePermisos();
  const panel = usePanelDeCertificados();
  // La consulta de la lista vive en la direccion, como en el inventario (decision 0076).
  const [parametros, setParametros] = useSearchParams();
  const consulta = desdeParametros(parametros, ConsultaDeCertificadosSchema);
  const {tipo} = consulta;
  const cambiar = (cambios: Partial<ConsultaDeCertificados>) =>
    setParametros(parametrosDeCertificados({...consulta, ...cambios}), {replace: true});
  const [buscar, setBuscar] = useState(consulta.buscar ?? '');
  useEffect(() => {
    const t = setTimeout(() => {
      if ((buscar.trim() || undefined) !== consulta.buscar) cambiar({buscar: buscar.trim() || undefined});
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscar]);
  const lista = useListaDeCertificados(consulta);
  const useOpciones: UseOpcionesDeColumna = (columna, contiene, habilitado) =>
    useOpcionesDeColumnaDeCertificados(consulta, columna, contiene, habilitado);
  const p = panel.data;

  return (
    <section className="max-w-6xl">
      <PestanasSeguimiento />
      <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-verde-oscuro">
            {tr('Certificados de origen y análisis', 'Origin and analysis certificates')}
          </h1>
          <p className="text-sm text-apagado">
            {tr(
              'Cada recolección, título minero y planta lleva su certificado de origen (con el consecutivo del título) y su certificado de análisis. Cada Big Bag queda ligado a los suyos.',
              'Each pickup, mining title and plant has its origin certificate (with the title’s consecutive number) and its analysis certificate. Each Big Bag is linked to its own.',
            )}
          </p>
        </div>
        <a href={RUTA_RELACION_POR_BIG_BAG} className={botonSuave}>
          {tr('⬇ Relación de certificados por Big Bag', '⬇ Certificates by Big Bag')}
        </a>
      </div>

      {p && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-5" data-prueba="totales-certificados">
          <Cifra titulo={tr('Certificados de origen', 'Origin certificates')} valor={p.totales.origen} />
          <Cifra titulo={tr('De boletines', 'From bulletins')} valor={p.totales.boletines} />
          <Cifra titulo={tr('Certificados de análisis', 'Analysis certificates')} valor={p.totales.analisis} />
          <Cifra titulo={tr('Big Bags con C.O.', 'Big Bags with C.O.')} valor={p.totales.bigBagsConOrigen} />
          <Cifra titulo={tr('Big Bags con análisis', 'Big Bags with analysis')} valor={p.totales.bigBagsConAnalisis} />
        </div>
      )}

      <h2 className="mt-6 text-lg font-medium">
        {tr('Consecutivos', 'Consecutive numbers')}
        <NotaDeAyuda texto={A.consecutivos} />
      </h2>
      <div className="mt-2 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {p?.titulos.map(t => (
          <TarjetaDeTitulo key={t.id} titulo={t} editar={permisos.editar} />
        ))}
        {p && p.series.length > 0 && (
          <div className="rounded-lg border border-linea bg-white p-4 text-sm">
            <div className="font-medium">
              {tr('Series de análisis', 'Analysis series')}
              <NotaDeAyuda texto={A.series} />
            </div>
            <table className="mt-2 w-full">
              <tbody>
                {p.series.map(s => (
                  <tr key={s.prefijo} className="border-t border-linea/60">
                    <td className="py-1 font-medium">{s.prefijo}</td>
                    <td className="py-1 text-apagado">
                      {tr('último', 'last')} {s.ultimoCodigo ?? '—'}
                    </td>
                    <td className="py-1 text-right">
                      {tr('sigue', 'next')} <span className="font-medium text-verde-oscuro">{s.siguiente}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {p && (
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <TarjetaDeComercializador datos={p.comercializador} editar={permisos.editar} />
          <div className="rounded-lg border border-linea bg-white p-4 text-sm">
            <div className="font-medium">
              {tr('Plantas de beneficio', 'Beneficiation plants')}
              <NotaDeAyuda texto={A.plantas} />
            </div>
            {p.plantas.map(planta => (
              <FilaDePlanta
                key={planta.id}
                id={planta.id}
                nombre={planta.nombre}
                perfil={planta.perfil}
                editar={permisos.editar}
              />
            ))}
          </div>
        </div>
      )}

      {permisos.editar && <CertificadosDeUnaExportacion />}
      {permisos.editar && <CrearDeUnaRecoleccion />}
      {permisos.editar && p && <RegistrarPorFuera titulos={p.titulos} />}
      <PaqueteDeUnaExportacion />
      {permisos.editar && <Cargas />}
      {permisos.administrar && <EliminarCertificados />}

      <h2 className="mt-6 text-lg font-medium">{tr('Certificados', 'Certificates')}</h2>
      <div className="mt-2 flex flex-wrap items-end gap-3">
        <div className="flex rounded-md border border-linea bg-white text-sm" role="group">
          {(['origen', 'analisis'] as const).map(t => (
            <button
              key={t}
              type="button"
              // Cada tipo tiene sus columnas: los filtros por columna se limpian.
              onClick={() => cambiar({tipo: t, porColumna: undefined})}
              className={`px-3 py-1.5 ${tipo === t ? 'bg-verde text-white' : 'hover:bg-papel'}`}
              aria-pressed={tipo === t}
            >
              {t === 'origen' ? tr('De origen', 'Origin') : tr('De análisis', 'Analysis')}
            </button>
          ))}
        </div>
        <label className="text-xs text-apagado">
          {tr('Buscar', 'Search')}
          <NotaDeAyuda texto={A.buscar} dentroDeEtiqueta />
          <input
            value={buscar}
            onChange={e => setBuscar(e.target.value)}
            className={campo}
            aria-label={tr('Buscar certificados', 'Search certificates')}
          />
        </label>
        {lista.data && (
          <span className="pb-1 text-sm text-apagado" data-prueba="total-de-certificados">
            {lista.data.total > consulta.limite
              ? tr(
                  `${numero(lista.data.total)} certificados; se muestran los ${numero(consulta.limite)} más recientes`,
                  `${numero(lista.data.total)} certificates; showing the ${numero(consulta.limite)} most recent`,
                )
              : tr(`${numero(lista.data.total)} certificados`, `${numero(lista.data.total)} certificates`)}
          </span>
        )}
        {(consulta.buscar || consulta.porColumna || consulta.titulo) && (
          <button
            type="button"
            className="pb-1 text-sm text-verde hover:underline"
            onClick={() => {
              setBuscar('');
              cambiar({buscar: undefined, porColumna: undefined, titulo: undefined});
            }}
          >
            {tr('Quitar filtros', 'Clear filters')}
          </button>
        )}
      </div>
      <FiltrosPorColumna
        columnas={tipo === 'origen' ? COLUMNAS_FILTRABLES_DE_ORIGEN : COLUMNAS_FILTRABLES_DE_ANALISIS}
        valor={consulta.porColumna}
        onCambiar={porColumna => cambiar({porColumna})}
        useOpciones={useOpciones}
        abiertos
        ayuda={tr(
          'Puedes marcar varios valores en cada uno; los números y las fechas van por rango (desde, hasta).',
          'You can tick several values in each one; numbers and dates go by range (from, to).',
        )}
      />
      <div className="mt-2 overflow-x-auto rounded-lg border border-linea bg-white">
        {tipo === 'origen' ? (
          <table className="w-full text-sm" aria-label={tr('Certificados de origen', 'Origin certificates')}>
            <thead className="bg-papel text-left text-xs text-apagado">
              <tr>
                <th className="px-3 py-2">{tr('Título', 'Title')}</th>
                <th className="px-3 py-2">No.</th>
                <th className="px-3 py-2">{tr('Recolección', 'Pickup')}</th>
                <th className="px-3 py-2">{tr('Planta', 'Plant')}</th>
                <th className="px-3 py-2 text-right">Au (g)</th>
                <th className="px-3 py-2 text-right">Ag (g)</th>
                <th className="px-3 py-2">{tr('Fecha', 'Date')}</th>
                <th className="px-3 py-2">{tr('Análisis', 'Analysis')}</th>
                <th className="px-3 py-2 text-right">BB</th>
                <th className="px-3 py-2">{tr('Exportaciones', 'Exports')}</th>
                <th className="px-3 py-2">
                  {tr('Descargar', 'Download')}
                  <NotaDeAyuda texto={A.descargarOrigen} />
                </th>
                {permisos.administrar && (
                  <th className="px-3 py-2">
                    {tr('Eliminar', 'Delete')}
                    <NotaDeAyuda texto={A.eliminarUno} />
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {lista.data?.origen.map(c => (
                <tr key={c.id} className="border-t border-linea/60">
                  <td className="px-3 py-1.5">{c.titulo}</td>
                  <td className="px-3 py-1.5 font-medium">{c.numero}</td>
                  <td className="px-3 py-1.5">
                    {c.boletin
                      ? tr(`Boletín ${c.boletin}`, `Bulletin ${c.boletin}`)
                      : `${textoOGuion(c.recoleccion)}${c.sublote ?? ''}`}
                  </td>
                  <td className="px-3 py-1.5">{textoOGuion(c.planta)}</td>
                  <td className="px-3 py-1.5 text-right">{numero(c.auG, 2)}</td>
                  <td className="px-3 py-1.5 text-right">{numero(c.agG, 2)}</td>
                  <td className="px-3 py-1.5">{fecha(c.fecha)}</td>
                  <td className="px-3 py-1.5">{c.analisis.join(', ') || '—'}</td>
                  <td className="px-3 py-1.5 text-right">{numero(c.bigBags)}</td>
                  <td className="px-3 py-1.5">{c.exportaciones.join(', ') || '—'}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    <a
                      href={rutaDeDocumentoDeOrigen(c.id, 'origen')}
                      className="text-verde hover:underline"
                      aria-label={tr(
                        `Certificado de origen ${c.titulo} ${c.numero}`,
                        `Origin certificate ${c.titulo} ${c.numero}`,
                      )}
                    >
                      ⬇ C.O.
                    </a>{' '}
                    ·{' '}
                    <a
                      href={rutaDeDocumentoDeOrigen(c.id, 'planta')}
                      className="text-verde hover:underline"
                      aria-label={tr(
                        `Certificado de planta de beneficio ${c.titulo} ${c.numero}`,
                        `Processing plant certificate ${c.titulo} ${c.numero}`,
                      )}
                    >
                      C.P.B.
                    </a>
                  </td>
                  {permisos.administrar && (
                    <td className="px-3 py-1.5">
                      <EliminarUno
                        tipo="origen"
                        id={c.id}
                        nombre={tr(`C.O. ${c.titulo} ${c.numero}`, `C.O. ${c.titulo} ${c.numero}`)}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table className="w-full text-sm" aria-label={tr('Certificados de análisis', 'Analysis certificates')}>
            <thead className="bg-papel text-left text-xs text-apagado">
              <tr>
                <th className="px-3 py-2">No.</th>
                <th className="px-3 py-2">{tr('Título', 'Title')}</th>
                <th className="px-3 py-2">{tr('Recolección', 'Pickup')}</th>
                <th className="px-3 py-2">{tr('Planta', 'Plant')}</th>
                <th className="px-3 py-2">C.O.</th>
                <th className="px-3 py-2 text-right">{tr('Ton secas', 'Dry tons')}</th>
                <th className="px-3 py-2 text-right">BB</th>
                <th className="px-3 py-2">{tr('Exportaciones', 'Exports')}</th>
                <th className="px-3 py-2">
                  {tr('Descargar', 'Download')}
                  <NotaDeAyuda texto={A.descargarAnalisis} />
                </th>
                {permisos.administrar && (
                  <th className="px-3 py-2">
                    {tr('Eliminar', 'Delete')}
                    <NotaDeAyuda texto={A.eliminarUno} />
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {lista.data?.analisis.map(c => (
                <tr key={c.id} className="border-t border-linea/60">
                  <td className="px-3 py-1.5 font-medium">
                    {/* Los de Colombian Mint no llevan numero: su serie (CM, QU...) (decision 0100). */}
                    {c.codigo ?? (c.serie && !c.titulo ? c.serie : '—')}
                  </td>
                  <td className="px-3 py-1.5">{textoOGuion(c.titulo)}</td>
                  <td className="px-3 py-1.5">{`${textoOGuion(c.recoleccion)}${c.sublote ?? ''}`}</td>
                  <td className="px-3 py-1.5">{textoOGuion(c.planta)}</td>
                  <td className="px-3 py-1.5">{c.origen ?? '—'}</td>
                  <td className="px-3 py-1.5 text-right">{numero(c.tonSecas, 2)}</td>
                  <td className="px-3 py-1.5 text-right">{numero(c.bigBags)}</td>
                  <td className="px-3 py-1.5">{c.exportaciones.join(', ') || '—'}</td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    <a
                      href={rutaDeDocumentoDeAnalisis(c.id)}
                      className="text-verde hover:underline"
                      aria-label={tr(
                        `Certificado de análisis ${c.codigo ?? ''}`,
                        `Analysis certificate ${c.codigo ?? ''}`,
                      )}
                    >
                      ⬇ Word
                    </a>
                    {!c.titulo && (
                      <a
                        href={rutaDeDocumentoDeAnalisis(c.id, 'pdf')}
                        className="ml-2 text-verde hover:underline"
                        aria-label={tr(
                          `Certificado de análisis ${c.serie ?? ''} ${c.recoleccion ?? ''} en PDF`,
                          `Analysis certificate ${c.serie ?? ''} ${c.recoleccion ?? ''} as PDF`,
                        )}
                      >
                        ⬇ PDF
                      </a>
                    )}
                  </td>
                  {permisos.administrar && (
                    <td className="px-3 py-1.5">
                      <EliminarUno tipo="analisis" id={c.id} nombre={c.codigo ?? tr('(sin número)', '(no number)')} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </section>
  );
}

// Eliminar un solo certificado de la lista (decision 0077), solo administradores.
function EliminarUno({tipo, id, nombre}: {tipo: 'origen' | 'analisis'; id: number; nombre: string}) {
  const eliminar = useEliminarCertificados();
  const pedido = (modo: 'revisar' | 'eliminar') => eliminar.mutateAsync({alcance: tipo, codigo: String(id), modo});
  return (
    <BotonDeEliminar
      etiqueta="🗑"
      titulo={tr(`Eliminar ${nombre}`, `Delete ${nombre}`)}
      siempreRevisar
      revisar={async () => {
        const r = await pedido('revisar');
        return {cantidad: r.cantidad, lineas: r.lineas};
      }}
      eliminar={async () => (await pedido('eliminar')).mensaje}
    />
  );
}

// Paquete de certificados de una exportacion (decision 0075): la tabla de la
// carta relacion, los parciales, los avisos y el ZIP para la VUCE.
function PaqueteDeUnaExportacion() {
  const [texto, setTexto] = useState('');
  const [exportacion, setExportacion] = useState<string | null>(null);
  const [fechaDeLaCarta, setFechaDeLaCarta] = useState('');
  const [radicado, setRadicado] = useState('');
  const paquete = usePaqueteDeCertificados(exportacion);
  const p = paquete.data;
  return (
    <div className="mt-6 rounded-lg border border-linea bg-white p-4 text-sm" data-prueba="paquete-de-certificados">
      <h2 className="text-lg font-medium">
        {tr('Paquete de certificados de una exportación', 'Certificate package of an export')}
        <NotaDeAyuda texto={A.paquete} />
      </h2>
      <form
        className="mt-2 flex flex-wrap items-end gap-3"
        onSubmit={e => {
          e.preventDefault();
          setExportacion(texto.trim().toUpperCase() || null);
        }}
      >
        <label className="text-xs text-apagado">
          {tr('Exportación', 'Export')}
          <input
            value={texto}
            onChange={e => setTexto(e.target.value)}
            placeholder="MM082026.100"
            className={campo}
            aria-label={tr('Exportación del paquete', 'Export of the package')}
          />
        </label>
        <button type="submit" className={botonSuave} disabled={!texto.trim() || paquete.isFetching}>
          {paquete.isFetching ? tr('Buscando…', 'Searching…') : tr('Ver sus certificados', 'See its certificates')}
        </button>
      </form>
      {paquete.isError && <p className="mt-2 text-alerta">{paquete.error.message}</p>}
      {p && (
        <div className="mt-3 space-y-3">
          <p>
            {tr(
              `${p.exportacion} (${p.nombreCorto}): ${p.certificadosDeOrigen} certificados de origen y ${p.certificadosDeAnalisis} de análisis. Radicado VUCE: ${p.radicado ?? 'sin radicado'}.`,
              `${p.exportacion} (${p.nombreCorto}): ${p.certificadosDeOrigen} origin and ${p.certificadosDeAnalisis} analysis certificates. VUCE filing: ${p.radicado ?? 'none'}.`,
            )}
          </p>
          {p.avisos.map(aviso => (
            <p key={aviso} className="text-alerta">
              ⚠ {aviso}
            </p>
          ))}
          {p.filas.length > 0 && (
            <div className="overflow-x-auto rounded-md border border-linea/80">
              <table className="w-full text-sm" aria-label={tr('Relación de certificados', 'Certificate relation')}>
                <thead className="bg-papel text-left text-xs text-apagado">
                  <tr>
                    <th className="px-3 py-2">{tr('Proveedor', 'Supplier')}</th>
                    <th className="px-3 py-2">{tr('Expediente', 'File')}</th>
                    <th className="px-3 py-2">{tr('Análisis', 'Analysis')}</th>
                    <th className="px-3 py-2">C.O.</th>
                    <th className="px-3 py-2 text-right">BB</th>
                    <th className="px-3 py-2 text-right">{tr('Ton del certificado', 'Certificate tons')}</th>
                    <th className="px-3 py-2 text-right">{tr('Ton de la exportación', 'Export tons')}</th>
                    <th className="px-3 py-2 text-right">{tr('Mineral del certificado', 'Certificate mineral')}</th>
                    <th className="px-3 py-2 text-right">{tr('Mineral de la exportación', 'Export mineral')}</th>
                  </tr>
                </thead>
                <tbody>
                  {p.filas.map(f => (
                    <tr
                      key={`${f.analisis}|${f.origen}`}
                      className={`border-t border-linea/60 ${f.parcial ? 'bg-dorado-claro' : ''}`}
                    >
                      <td className="px-3 py-1.5">{textoOGuion(f.proveedor)}</td>
                      <td className="px-3 py-1.5">{textoOGuion(f.expediente)}</td>
                      <td className="px-3 py-1.5 font-medium">{textoOGuion(f.analisis)}</td>
                      <td className="px-3 py-1.5">{f.origen ?? '—'}</td>
                      <td className="px-3 py-1.5 text-right">{numero(f.bigBags)}</td>
                      <td className="px-3 py-1.5 text-right">{numero(f.tonCertificado, 2)}</td>
                      <td className="px-3 py-1.5 text-right">{numero(f.tonExportacion, 2)}</td>
                      <td className="px-3 py-1.5 text-right">{numero(f.mineralCertificado, 2)}</td>
                      <td className="px-3 py-1.5 text-right">{numero(f.mineralExportacion, 2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {p.parciales && (
            <p className="text-apagado">
              <span className="font-medium text-tinta">
                {tr('Parciales en la carta: ', 'Partials in the letter: ')}
              </span>
              {p.parciales}
            </p>
          )}
          {(p.certificadosDeOrigen > 0 || p.certificadosDeAnalisis > 0) && (
            <div className="flex flex-wrap items-end gap-3">
              <label className="text-xs text-apagado">
                {tr('Fecha de la carta', 'Letter date')}
                <input
                  type="date"
                  value={fechaDeLaCarta}
                  onChange={e => setFechaDeLaCarta(e.target.value)}
                  className={campo}
                  aria-label={tr('Fecha de la carta', 'Letter date')}
                />
              </label>
              <label className="text-xs text-apagado">
                {tr('Radicado VUCE (TM-…)', 'VUCE filing (TM-…)')}
                <input
                  value={radicado}
                  onChange={e => setRadicado(e.target.value)}
                  placeholder={p.radicado ?? 'TM-00000000-20260101'}
                  className={campo}
                  aria-label={tr('Radicado VUCE de la carta', 'VUCE filing of the letter')}
                />
              </label>
              <a href={rutaDelPaqueteDeCertificados(p.exportacion, fechaDeLaCarta, radicado)} className={boton}>
                {tr('⬇ Descargar el paquete (ZIP)', '⬇ Download the package (ZIP)')}
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const FORMULARIO_POR_FUERA = {
  tituloId: '',
  numero: '',
  analisis: '',
  recoleccion: '',
  sublote: '',
  fecha: '',
  auG: '',
  agG: '',
  bigBags: '',
  notas: '',
};

// Un certificado hecho por fuera de la app (decision 0097): su numero queda
// tomado y, si se dicen, sus Big Bags quedan ligados.
function RegistrarPorFuera({titulos}: {titulos: TituloParaCertificados[]}) {
  const registrar = useRegistrarCertificadoPorFuera();
  const [abierto, setAbierto] = useState(false);
  const [f, setF] = useState(FORMULARIO_POR_FUERA);
  const [mensaje, setMensaje] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const titulo = titulos.find(t => String(t.id) === f.tituloId);
  const cambiar = (campoDe: keyof typeof FORMULARIO_POR_FUERA) => (valor: string) => setF({...f, [campoDe]: valor});
  const texto = (v: string) => v.trim() || null;
  const cifra = (v: string) => (v.trim() ? Number(v.replace(',', '.')) : null);
  const puedeGuardar = !!titulo && (!!f.numero.trim() || !!f.analisis.trim()) && !registrar.isPending;
  const entrada = (
    clave: keyof typeof FORMULARIO_POR_FUERA,
    etiqueta: string,
    propiedades: {type?: string; placeholder?: string; step?: string} = {},
  ) => (
    <label className="text-xs text-apagado">
      {etiqueta}
      <input
        {...propiedades}
        value={f[clave]}
        onChange={e => cambiar(clave)(e.target.value)}
        className={campo}
        aria-label={etiqueta}
      />
    </label>
  );
  return (
    <div className="mt-6 rounded-lg border border-linea bg-white p-4 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="font-medium">
          {tr('Registrar certificado hecho por fuera', 'Register a certificate made outside the app')}
          <NotaDeAyuda texto={A.porFuera} />
        </div>
        <button type="button" className={botonSuave} onClick={() => setAbierto(!abierto)} aria-expanded={abierto}>
          {abierto ? tr('Cerrar', 'Close') : tr('＋ Registrar', '＋ Register')}
        </button>
      </div>
      {mensaje && (
        <p className="mt-2 text-verde-oscuro" role="status">
          {mensaje}
        </p>
      )}
      {abierto && (
        <form
          className="mt-3 grid gap-3 md:grid-cols-3"
          onSubmit={e => {
            e.preventDefault();
            setError(null);
            setMensaje(null);
            registrar
              .mutateAsync({
                tituloId: Number(f.tituloId),
                numero: f.numero.trim() ? Number(f.numero) : null,
                analisis: texto(f.analisis),
                recoleccion: texto(f.recoleccion),
                sublote: texto(f.sublote),
                fecha: texto(f.fecha),
                auG: cifra(f.auG),
                agG: cifra(f.agG),
                bigBags: f.bigBags.split(/[\s,;]+/).filter(Boolean),
                notas: texto(f.notas),
              })
              .then(
                r => {
                  setMensaje(r.mensaje);
                  setF(FORMULARIO_POR_FUERA);
                  setAbierto(false);
                },
                (err: unknown) =>
                  setError(err instanceof ErrorDeApi ? err.message : tr('No se pudo guardar.', 'Could not save.')),
              );
          }}
        >
          <label className="text-xs text-apagado">
            {tr('Título minero', 'Mining title')}
            <select
              value={f.tituloId}
              onChange={e => cambiar('tituloId')(e.target.value)}
              className={campo}
              aria-label={tr('Título minero', 'Mining title')}
            >
              <option value="">{tr('Elegir', 'Choose')}</option>
              {titulos.map(t => (
                <option key={t.id} value={t.id}>
                  {t.codigo}
                </option>
              ))}
            </select>
          </label>
          {entrada('numero', tr('No. del certificado de origen', 'Origin certificate No.'), {
            type: 'number',
            placeholder: titulo
              ? tr(`Sigue ${titulo.siguienteConsecutivo}`, `Next ${titulo.siguienteConsecutivo}`)
              : '',
          })}
          {entrada('analisis', tr('No. del certificado de análisis', 'Analysis certificate No.'), {
            placeholder: 'MM-399',
          })}
          {entrada('recoleccion', tr('Recolección', 'Pickup'), {placeholder: 'S-606'})}
          {entrada('sublote', tr('Letra de la recolección', 'Pickup letter'), {placeholder: 'A'})}
          {entrada('fecha', tr('Fecha del certificado', 'Certificate date'), {type: 'date'})}
          {entrada('auG', tr('Oro (g)', 'Gold (g)'), {type: 'number', step: 'any'})}
          {entrada('agG', tr('Plata (g)', 'Silver (g)'), {type: 'number', step: 'any'})}
          {entrada('notas', tr('Notas', 'Notes'))}
          <label className="text-xs text-apagado md:col-span-3">
            {tr(
              'Big Bags (separados por espacio, coma o renglón; opcional)',
              'Big Bags (separated by space, comma or line; optional)',
            )}
            <textarea
              rows={2}
              value={f.bigBags}
              onChange={e => cambiar('bigBags')(e.target.value)}
              className={campo}
              aria-label={tr('Big Bags del certificado', 'Big Bags of the certificate')}
            />
          </label>
          {error && <p className="text-sm text-alerta md:col-span-3">{error}</p>}
          <div className="md:col-span-3">
            <button type="submit" className={boton} disabled={!puedeGuardar}>
              {tr('Guardar', 'Save')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// Eliminar los certificados de una recoleccion o de una exportacion (decision
// 0074), solo administradores: primero muestra que se borraria.
function EliminarCertificados() {
  const [alcance, setAlcance] = useState<AlcanceDeEliminarCertificados>('recoleccion');
  const [codigo, setCodigo] = useState('');
  const eliminar = useEliminarCertificados();
  const pedido = (modo: 'revisar' | 'eliminar') => eliminar.mutateAsync({alcance, codigo: codigo.trim(), modo});
  return (
    <div className="mt-6 rounded-lg border border-linea bg-white p-4 text-sm">
      <div className="font-medium">
        {tr('Eliminar certificados', 'Delete certificates')}
        <NotaDeAyuda texto={A.eliminar} />
      </div>
      <div className="mt-2 flex flex-wrap items-end gap-3">
        <label className="text-xs text-apagado">
          {tr('De', 'Of')}
          <select
            value={alcance}
            onChange={e => setAlcance(e.target.value as AlcanceDeEliminarCertificados)}
            className={campo}
            aria-label={tr('Eliminar los certificados de', 'Delete the certificates of')}
          >
            <option value="recoleccion">{tr('Una recolección', 'A pickup')}</option>
            <option value="exportacion">{tr('Una exportación', 'An export')}</option>
          </select>
        </label>
        <label className="text-xs text-apagado">
          {alcance === 'recoleccion' ? tr('Recolección', 'Pickup') : tr('Exportación', 'Export')}
          <input
            value={codigo}
            onChange={e => setCodigo(e.target.value)}
            placeholder={alcance === 'recoleccion' ? 'S-606' : 'MM102026.106'}
            className={campo}
            aria-label={tr('Código para eliminar sus certificados', 'Code to delete its certificates')}
          />
        </label>
        <span className="flex flex-wrap items-center gap-2 pb-1">
          <BotonDeEliminar
            etiqueta={tr('🗑 Eliminar sus certificados', '🗑 Delete its certificates')}
            titulo={tr('Eliminar certificados', 'Delete certificates')}
            deshabilitado={!codigo.trim()}
            siempreRevisar
            revisar={async () => {
              const r = await pedido('revisar');
              return {cantidad: r.cantidad, lineas: r.lineas};
            }}
            eliminar={async () => (await pedido('eliminar')).mensaje}
            onTerminado={() => setCodigo('')}
          />
        </span>
      </div>
    </div>
  );
}

function Cifra({titulo, valor}: {titulo: string; valor: number}) {
  return (
    <div className="rounded-lg border border-linea bg-white p-3">
      <div className="text-xs text-apagado">{titulo}</div>
      <div className="text-xl font-semibold text-verde-oscuro">{numero(valor)}</div>
    </div>
  );
}

function TarjetaDeTitulo({titulo, editar}: {titulo: TituloParaCertificados; editar: boolean}) {
  const guardar = useGuardarPerfilDeTitulo();
  const [editando, setEditando] = useState<PerfilDeTitulo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const t = titulo;
  return (
    <div className="rounded-lg border border-linea bg-white p-4 text-sm" data-prueba={`titulo-${t.codigo}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="font-medium">{t.codigo}</div>
          <div className="text-xs text-apagado">{[t.mina, t.perfil.explotador].filter(Boolean).join(' · ') || '—'}</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-apagado">{tr('Siguiente C.O.', 'Next C.O.')}</div>
          <div className="text-xl font-semibold text-verde-oscuro">{t.siguienteConsecutivo}</div>
        </div>
      </div>
      <div className="mt-1 text-xs text-apagado">
        {tr(
          `Último: ${t.ultimoConsecutivo} · ${numero(t.certificados)} certificados en la app`,
          `Last: ${t.ultimoConsecutivo} · ${numero(t.certificados)} certificates in the app`,
        )}
      </div>
      {editar && !editando && (
        <button type="button" className={`${botonSuave} mt-2`} onClick={() => setEditando({...t.perfil})}>
          {tr('Editar datos del título', 'Edit title details')}
        </button>
      )}
      {editando && (
        <form
          className="mt-2 grid gap-2"
          onSubmit={e => {
            e.preventDefault();
            setError(null);
            guardar.mutateAsync({id: t.id, perfil: editando}).then(
              () => setEditando(null),
              (err: unknown) =>
                setError(err instanceof ErrorDeApi ? err.message : tr('No se pudo guardar.', 'Could not save.')),
            );
          }}
        >
          {CAMPOS_DE_TITULO.map(([clave, etiqueta]) => (
            <label key={clave} className="text-xs text-apagado">
              {trx(etiqueta)}
              <input
                value={(editando[clave] as string | undefined) ?? ''}
                onChange={e => setEditando({...editando, [clave]: e.target.value || undefined})}
                className={campo}
                aria-label={trx(etiqueta)}
              />
            </label>
          ))}
          <label className="text-xs text-apagado">
            {tr('El comprador es', 'The buyer is')}
            <NotaDeAyuda texto={A.compradorTipo} dentroDeEtiqueta />
            <select
              value={editando.compradorTipo ?? ''}
              onChange={e =>
                setEditando({
                  ...editando,
                  compradorTipo: (e.target.value || undefined) as PerfilDeTitulo['compradorTipo'],
                })
              }
              className={campo}
              aria-label={tr('El comprador es', 'The buyer is')}
            >
              <option value="">
                {tr('Según el RUCOM (sin RUCOM = consumidor)', 'By RUCOM (no RUCOM = consumer)')}
              </option>
              <option value="comercializador">{tr('Comercializador', 'Trader')}</option>
              <option value="consumidor">{tr('Consumidor', 'Consumer')}</option>
            </select>
          </label>
          <label className="text-xs text-apagado">
            {tr('Último consecutivo usado fuera de la app', 'Last number used outside the app')}
            <NotaDeAyuda texto={A.ultimoConsecutivo} dentroDeEtiqueta />
            <input
              type="number"
              min={0}
              value={editando.ultimoConsecutivo ?? ''}
              onChange={e =>
                setEditando({...editando, ultimoConsecutivo: e.target.value ? Number(e.target.value) : undefined})
              }
              className={campo}
              aria-label={tr('Último consecutivo usado fuera de la app', 'Last number used outside the app')}
            />
          </label>
          {error && <p className="text-sm text-alerta">{error}</p>}
          <div className="flex gap-2">
            <button type="submit" className={boton} disabled={guardar.isPending}>
              {tr('Guardar', 'Save')}
            </button>
            <button type="button" className={botonSuave} onClick={() => setEditando(null)}>
              {tr('Cancelar', 'Cancel')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function TarjetaDeComercializador({datos, editar}: {datos: Comercializador; editar: boolean}) {
  const guardar = useGuardarComercializador();
  const [editando, setEditando] = useState<Comercializador | null>(null);
  const filas: Array<[keyof Comercializador, string]> = [
    ['razonSocial', tr('Razón social', 'Company name')],
    ['nit', 'NIT'],
    ['rucom', 'RUCOM'],
    ['direccion', tr('Dirección', 'Address')],
  ];
  return (
    <div className="rounded-lg border border-linea bg-white p-4 text-sm">
      <div className="font-medium">
        {tr('Comercializador', 'Trader')}
        <NotaDeAyuda texto={A.comercializador} />
      </div>
      {!editando ? (
        <>
          {filas.map(([clave, etiqueta]) => (
            <div key={clave} className="mt-1">
              <span className="text-apagado">{etiqueta}:</span> {datos[clave] ?? '—'}
            </div>
          ))}
          {editar && (
            <button type="button" className={`${botonSuave} mt-2`} onClick={() => setEditando({...datos})}>
              {tr('Editar', 'Edit')}
            </button>
          )}
        </>
      ) : (
        <form
          className="mt-2 grid gap-2"
          onSubmit={e => {
            e.preventDefault();
            void guardar.mutateAsync(editando).then(() => setEditando(null));
          }}
        >
          {filas.map(([clave, etiqueta]) => (
            <label key={clave} className="text-xs text-apagado">
              {etiqueta}
              <input
                value={editando[clave] ?? ''}
                onChange={e => setEditando({...editando, [clave]: e.target.value || null})}
                className={campo}
                aria-label={etiqueta}
              />
            </label>
          ))}
          <div className="flex gap-2">
            <button type="submit" className={boton} disabled={guardar.isPending}>
              {tr('Guardar', 'Save')}
            </button>
            <button type="button" className={botonSuave} onClick={() => setEditando(null)}>
              {tr('Cancelar', 'Cancel')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function FilaDePlanta({
  id,
  nombre,
  perfil,
  editar,
}: {
  id: number;
  nombre: string;
  perfil: PerfilDePlanta;
  editar: boolean;
}) {
  const guardar = useGuardarPerfilDePlanta();
  const [editando, setEditando] = useState<PerfilDePlanta | null>(null);
  return (
    <div className="mt-2 border-t border-linea/60 pt-2">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{nombre}</span>
        {editar && !editando && (
          <button type="button" className="text-xs text-verde hover:underline" onClick={() => setEditando({...perfil})}>
            {tr('Editar', 'Edit')}
          </button>
        )}
      </div>
      {!editando ? (
        <div className="text-xs text-apagado">
          {[perfil.razonSocial, perfil.nit && `NIT ${perfil.nit}`, perfil.rucom && `RUCOM ${perfil.rucom}`]
            .filter(Boolean)
            .join(' · ') || tr('Sin datos para los certificados', 'No certificate details')}
        </div>
      ) : (
        <form
          className="mt-1 grid gap-1 sm:grid-cols-3"
          onSubmit={e => {
            e.preventDefault();
            void guardar.mutateAsync({id, perfil: editando}).then(() => setEditando(null));
          }}
        >
          {(
            [
              ['razonSocial', tr('Razón social', 'Company name')],
              ['nit', 'NIT'],
              ['rucom', 'RUCOM'],
            ] as Array<[keyof PerfilDePlanta, string]>
          ).map(([clave, etiqueta]) => (
            <input
              key={clave}
              placeholder={etiqueta}
              value={editando[clave] ?? ''}
              onChange={e => setEditando({...editando, [clave]: e.target.value || undefined})}
              className={campo}
              aria-label={`${etiqueta} ${nombre}`}
            />
          ))}
          <div className="flex gap-2 sm:col-span-3">
            <button type="submit" className={boton} disabled={guardar.isPending}>
              {tr('Guardar', 'Save')}
            </button>
            <button type="button" className={botonSuave} onClick={() => setEditando(null)}>
              {tr('Cancelar', 'Cancel')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function Cargas() {
  const cargar = useCargarCertificados();
  const [tipo, setTipo] = useState<TipoDeCargaDeCertificados>('generador');
  const [archivos, setArchivos] = useState<File[]>([]);
  const [resultado, setResultado] = useState<ResultadoDeCargaDeCertificados | null>(null);
  const [error, setError] = useState<string | null>(null);
  const leerExpedientes = useLeerFacturasDeExpedientes();
  const [lectura, setLectura] = useState<ResultadoDeCargaDeCertificados | null>(null);
  // Certificados de Colombian Mint de la carpeta Recolecciones (decision 0100): por partes.
  const porCarpeta = tipo === 'analisis';
  const [avance, setAvance] = useState<string | null>(null);
  const clienteDeConsultas = useQueryClient();
  const enviar = (modo: 'revisar' | 'aplicar') => {
    setError(null);
    const fallo = (err: unknown) =>
      setError(err instanceof ErrorDeApi ? err.message : tr('No se pudo leer el archivo.', 'Could not read the file.'));
    if (porCarpeta) {
      setAvance(tr('Empezando…', 'Starting…'));
      void cargarAnalisisPorLotes(archivos, modo, (hechos, total) =>
        setAvance(tr(`Parte ${hechos} de ${total}…`, `Part ${hechos} of ${total}…`)),
      )
        .then(async r => {
          setResultado(r);
          if (r.aplicado) await clienteDeConsultas.invalidateQueries({queryKey: ['certificados']});
        }, fallo)
        .finally(() => setAvance(null));
      return;
    }
    cargar.mutateAsync({tipo, archivos, modo}).then(setResultado, fallo);
  };
  const ocupado = cargar.isPending || avance !== null;
  return (
    <div className="mt-6 rounded-lg border border-linea bg-white p-4 text-sm" data-prueba="cargas-certificados">
      <h2 className="text-lg font-medium">
        {tr('Cargar archivos de certificados', 'Upload certificate files')}
        <NotaDeAyuda texto={A.cargas} />
      </h2>
      <div className="mt-2 flex flex-wrap items-end gap-3">
        <label className="text-xs text-apagado">
          {tr('Archivo', 'File')}
          <select
            value={tipo}
            onChange={e => {
              setTipo(e.target.value as TipoDeCargaDeCertificados);
              setResultado(null);
            }}
            className={campo}
            aria-label={tr('Tipo de archivo', 'File type')}
          >
            {TIPOS_DE_CARGA_DE_CERTIFICADOS.map(t => (
              <option key={t} value={t}>
                {trx(NOMBRE_DE_CARGA[t])}
              </option>
            ))}
          </select>
        </label>
        {porCarpeta ? (
          <label className={`${botonSuave} cursor-pointer`}>
            {tr('Elegir la carpeta Recolecciones', 'Choose the Recolecciones folder')}
            <input
              type="file"
              multiple
              className="sr-only"
              aria-label={tr('Carpeta Recolecciones', 'Recolecciones folder')}
              {...{webkitdirectory: '', directory: ''}}
              onChange={e => {
                setArchivos(certificadosDeColombianMint([...(e.target.files ?? [])]));
                setResultado(null);
                e.target.value = '';
              }}
            />
          </label>
        ) : (
          <ZonaParaSoltar className="flex flex-col gap-1">
            <input
              type="file"
              accept={tipo === 'facturas' ? '.pdf' : '.xlsx,.xlsm'}
              multiple={tipo === 'control' || tipo === 'facturas'}
              aria-label={tr('Archivos de certificados', 'Certificate files')}
              onChange={e => {
                setArchivos([...(e.target.files ?? [])]);
                setResultado(null);
              }}
            />
            {!archivos.length && <PistaDeSoltar multiple={tipo === 'control' || tipo === 'facturas'} />}
          </ZonaParaSoltar>
        )}
        <button
          type="button"
          className={botonSuave}
          disabled={!archivos.length || ocupado}
          onClick={() => enviar('revisar')}
        >
          {ocupado ? (avance ?? tr('Leyendo…', 'Reading…')) : tr('Revisar', 'Review')}
        </button>
        {resultado && !resultado.aplicado && (
          <button type="button" className={boton} disabled={ocupado} onClick={() => enviar('aplicar')}>
            {tr('Guardar', 'Save')}
          </button>
        )}
      </div>
      {porCarpeta && (
        <p className="mt-1 text-xs text-apagado" data-prueba="analisis-elegidos">
          {archivos.length
            ? tr(
                `${numero(archivos.length)} archivos de certificados de Colombian Mint desde 2026 (Word y PDF). Los de Mona Minas y los anteriores a 2026 no se toman.`,
                `${numero(archivos.length)} Colombian Mint certificate files from 2026 on (Word and PDF). Mona Minas ones and those before 2026 are not taken.`,
              )
            : tr(
                'Elige la carpeta Storage\\Recolecciones de Dropbox: la app toma de sus subcarpetas los "CERTIFICADO DE ANÁLISIS INTERNO CM/QU/GP… S-###" en Word y PDF desde 2026, sin cambiar nada en Dropbox.',
                'Choose the Storage\\Recolecciones folder in Dropbox: the app takes from its subfolders the "CERTIFICADO DE ANÁLISIS INTERNO CM/QU/GP… S-###" in Word and PDF from 2026 on, without changing anything in Dropbox.',
              )}
        </p>
      )}
      <p className="mt-1 text-xs text-apagado">{trx(A.orden)}</p>
      {error && <p className="mt-2 text-alerta">{error}</p>}
      {resultado && (
        <div className="mt-3 rounded-md bg-papel p-3" data-prueba="resultado-certificados">
          <p className={resultado.aplicado ? 'font-medium text-verde-oscuro' : ''}>{resultado.mensaje}</p>
          {resultado.archivos.map(a => (
            <div key={`${a.archivo}-${a.exportacion}`} className="mt-1 text-xs">
              {a.archivo}
              {a.exportacion ? ` · ${a.exportacion}` : ''} · {tr(`${a.filas} filas`, `${a.filas} rows`)}
              {a.avisos.map(x => (
                <div key={x} className="text-dorado">
                  {x}
                </div>
              ))}
            </div>
          ))}
          {resultado.avisos.length > 0 && (
            <ul className="mt-2 list-disc pl-5 text-xs text-dorado">
              {resultado.avisos.map(x => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          )}
          {resultado.sinEncontrar.length > 0 && (
            <div className="mt-2 text-xs">
              <span className="font-medium">{tr('No están en la app:', 'Not in the app:')}</span>{' '}
              {resultado.sinEncontrar.join(', ')}
            </div>
          )}
        </div>
      )}
      <div className="mt-4 border-t border-linea/60 pt-3" data-prueba="facturas-de-expedientes">
        <div className="font-medium">
          {tr('Facturas que ya están en los expedientes', 'Invoices already in the export files')}
          <NotaDeAyuda texto={A.facturas} />
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={botonSuave}
            disabled={leerExpedientes.isPending}
            onClick={() =>
              leerExpedientes
                .mutateAsync('revisar')
                .then(setLectura, (err: unknown) =>
                  setError(err instanceof ErrorDeApi ? err.message : tr('No se pudo leer.', 'Could not read.')),
                )
            }
          >
            {leerExpedientes.isPending ? tr('Leyendo…', 'Reading…') : tr('Revisar', 'Review')}
          </button>
          {lectura && !lectura.aplicado && (
            <button
              type="button"
              className={boton}
              disabled={leerExpedientes.isPending}
              onClick={() => void leerExpedientes.mutateAsync('aplicar').then(setLectura)}
            >
              {tr('Guardar', 'Save')}
            </button>
          )}
        </div>
        {lectura && (
          <div className="mt-2 rounded-md bg-papel p-3 text-xs">
            <p className={lectura.aplicado ? 'font-medium text-verde-oscuro' : ''}>{lectura.mensaje}</p>
            {lectura.avisos.length > 0 && (
              <ul className="mt-1 list-disc pl-5 text-dorado">
                {lectura.avisos.map(x => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

type FilaParaCrear = {
  numero: string;
  analisis: string;
  fecha: string;
  comercializado: string;
  incluir: boolean;
  // Ligar al C.O. que coincide por el oro (decision 0077).
  ligar: boolean;
} & {[K in keyof FechasDelAnalisis]: string};

const CAMPOS_DE_FECHAS: Array<[keyof FechasDelAnalisis, ReturnType<typeof txt>]> = [
  ['recepcionDesde', txt('Recepción desde', 'Reception from')],
  ['recepcionHasta', txt('Recepción hasta', 'Reception to')],
  ['ensayoDesde', txt('Ensayo desde', 'Assay from')],
  ['ensayoHasta', txt('Ensayo hasta', 'Assay to')],
  ['emitido', txt('Emisión', 'Issued')],
];

// Lo mismo que revisa la API: el ensayo termina despues de la recepcion.
function problemaDeFechas(f: FilaParaCrear): string | null {
  if (CAMPOS_DE_FECHAS.some(([c]) => !f[c])) return tr('Faltan fechas del análisis.', 'Analysis dates are missing.');
  if (f.recepcionDesde > f.recepcionHasta || f.ensayoDesde > f.ensayoHasta)
    return tr('Una fecha "hasta" es anterior a su "desde".', 'A "to" date is before its "from".');
  if (f.ensayoHasta <= f.recepcionHasta)
    return tr('El ensayo debe terminar después de la recepción.', 'The assay must end after the reception.');
  if (f.emitido < f.ensayoHasta)
    return tr(
      'La emisión no puede ser antes del final del ensayo.',
      'The issue date cannot be before the end of the assay.',
    );
  return null;
}

// Crear los certificados de una recoleccion (decision 0072): un C.O. y un
// analisis por titulo y planta (Mona Minas), y solo el analisis para los Big
// Bags sin titulo (Colombian Mint), con los numeros y fechas que siguen.
function CrearDeUnaRecoleccion() {
  const [texto, setTexto] = useState('');
  const [recoleccion, setRecoleccion] = useState<string | null>(null);
  const propuesta = usePropuestaDeRecoleccion(recoleccion);
  const crear = useCrearCertificadosDeRecoleccion();
  const [filas, setFilas] = useState<Record<string, FilaParaCrear>>({});
  const [resultado, setResultado] = useState<ResultadoDeCertificadosDeRecoleccion | null>(null);
  const [error, setError] = useState<string | null>(null);
  const p = propuesta.data;
  const hecho = (g: GrupoDeRecoleccion) => (g.soloAnalisis ? g.conAnalisis : g.conOrigen) >= g.bigBags;
  const fila = (g: GrupoDeRecoleccion): FilaParaCrear =>
    filas[g.clave] ?? {
      numero: g.numeroSugerido === null ? '' : String(g.numeroSugerido),
      analisis: g.analisisSugerido ?? '',
      fecha: g.mesDeMolienda ?? '',
      comercializado: '',
      incluir: !hecho(g),
      ligar: g.coincide !== null,
      recepcionDesde: g.fechasSugeridas.recepcionDesde ?? '',
      recepcionHasta: g.fechasSugeridas.recepcionHasta ?? '',
      ensayoDesde: g.fechasSugeridas.ensayoDesde ?? '',
      ensayoHasta: g.fechasSugeridas.ensayoHasta ?? '',
      emitido: g.fechasSugeridas.emitido ?? '',
    };
  const cambiar = (g: GrupoDeRecoleccion, cambio: Partial<FilaParaCrear>) =>
    setFilas(antes => ({...antes, [g.clave]: {...fila(g), ...cambio}}));
  const elegidos = p ? p.grupos.filter(g => fila(g).incluir && !hecho(g)) : [];
  const incompleto = elegidos.some(g => {
    const f = fila(g);
    if (g.coincide && f.ligar) return false;
    return problemaDeFechas(f) !== null || (!g.soloAnalisis && (!f.numero || !f.fecha));
  });

  const confirmar = () => {
    if (!p) return;
    setError(null);
    crear
      .mutateAsync({
        recoleccion: p.recoleccion,
        peticion: {
          grupos: elegidos.map(g => {
            const f = fila(g);
            return {
              clave: g.clave,
              ligar: g.coincide !== null && f.ligar,
              numero: g.soloAnalisis ? null : Number(f.numero),
              analisis: f.analisis.trim() || null,
              fecha: g.soloAnalisis ? null : f.fecha,
              comercializado: g.soloAnalisis ? null : f.comercializado || null,
              fechasDelAnalisis: {
                recepcionDesde: f.recepcionDesde,
                recepcionHasta: f.recepcionHasta,
                ensayoDesde: f.ensayoDesde,
                ensayoHasta: f.ensayoHasta,
                emitido: f.emitido,
              },
            };
          }),
        },
      })
      .then(
        r => {
          setResultado(r);
          setFilas({});
        },
        (err: unknown) =>
          setError(err instanceof ErrorDeApi ? err.message : tr('No se pudieron crear.', 'Could not create them.')),
      );
  };

  return (
    <div className="mt-6 rounded-lg border border-linea bg-white p-4 text-sm" data-prueba="crear-certificados">
      <h2 className="text-lg font-medium">
        {tr('Crear los certificados de una recolección', 'Create the certificates of a pickup')}
        <NotaDeAyuda texto={A.crear} />
      </h2>
      <form
        className="mt-2 flex flex-wrap items-end gap-3"
        onSubmit={e => {
          e.preventDefault();
          setResultado(null);
          setFilas({});
          setRecoleccion(texto.trim() || null);
        }}
      >
        <label className="text-xs text-apagado">
          {tr('Recolección', 'Pickup')}
          <input
            value={texto}
            onChange={e => setTexto(e.target.value)}
            placeholder="S-606"
            className={campo}
            aria-label={tr('Recolección', 'Pickup')}
          />
        </label>
        <button type="submit" className={botonSuave} disabled={!texto.trim() || propuesta.isFetching}>
          {propuesta.isFetching ? tr('Buscando…', 'Searching…') : tr('Ver sus Big Bags', 'See its Big Bags')}
        </button>
      </form>
      {propuesta.isError && <p className="mt-2 text-alerta">{propuesta.error.message}</p>}
      {p && (
        <div className="mt-3 space-y-3" data-prueba="propuesta-de-recoleccion">
          {p.grupos.map(g => {
            const f = fila(g);
            const listo = hecho(g);
            const nombre = g.soloAnalisis
              ? [g.prefijo, g.exportador].filter(Boolean).join(' · ') || tr('Sin título minero', 'No mining title')
              : `${g.titulo}${g.planta ? ` · ${g.planta}` : ''}${g.sublote ? ` (${p.recoleccion}${g.sublote})` : ''}`;
            const enExportacion = g.exportacion ?? tr('sin exportación', 'no export');
            const ligando = g.coincide !== null && f.ligar;
            const problema = !listo && f.incluir && !ligando ? problemaDeFechas(f) : null;
            return (
              <div key={g.clave} className="rounded-md border border-linea/80 p-3" data-prueba="grupo-de-recoleccion">
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                  <label className="inline-flex items-center gap-2 font-medium">
                    <input
                      type="checkbox"
                      disabled={listo}
                      checked={!listo && f.incluir}
                      onChange={e => cambiar(g, {incluir: e.target.checked})}
                      aria-label={tr(`Crear ${nombre}`, `Create ${nombre}`)}
                    />
                    {nombre}
                  </label>
                  <span className="text-xs text-apagado">
                    {g.soloAnalisis
                      ? tr(
                          'Solo certificado de análisis (tabla por Big Bag)',
                          'Analysis certificate only (table by Big Bag)',
                        )
                      : tr('Certificado de origen y de análisis', 'Origin and analysis certificate')}
                  </span>
                  <span className="text-xs tabular-nums">
                    {numero(g.bigBags)} BB · Au {numero(g.auG, 2)} g · Ag {numero(g.agG, 2)} g
                  </span>
                  <span className="text-xs font-medium text-verde-oscuro" data-prueba="exportacion-del-grupo">
                    {tr(`Exportación: ${enExportacion}`, `Export: ${enExportacion}`)}
                  </span>
                </div>
                {(g.soloAnalisis ? g.conAnalisis : g.conOrigen) > 0 && (
                  <div className="mt-1 text-xs text-apagado">
                    {listo
                      ? tr(
                          `Ya tiene ${g.origenExistente ? `C.O. ${g.origenExistente}` : ''} ${g.analisisExistente ? `· ${g.analisisExistente}` : ''}`,
                          `Already has ${g.origenExistente ? `C.O. ${g.origenExistente}` : ''} ${g.analisisExistente ? `· ${g.analisisExistente}` : ''}`,
                        )
                      : tr(
                          `${g.soloAnalisis ? g.conAnalisis : g.conOrigen} Big Bags ya tienen certificado`,
                          `${g.soloAnalisis ? g.conAnalisis : g.conOrigen} Big Bags already have a certificate`,
                        )}
                  </div>
                )}
                {!listo && g.coincide && (
                  <label className="mt-2 flex items-center gap-2 text-xs" data-prueba="ligar-al-que-coincide">
                    <input type="checkbox" checked={f.ligar} onChange={e => cambiar(g, {ligar: e.target.checked})} />
                    {tr(
                      `Ligar al C.O. ${g.coincide.numero}${g.coincide.sublote ? ` (${p.recoleccion}${g.coincide.sublote})` : ''}${g.coincide.analisis ? ` · ${g.coincide.analisis}` : ''}: tiene el mismo oro y aún no tiene Big Bags en la app`,
                      `Link to C.O. ${g.coincide.numero}${g.coincide.sublote ? ` (${p.recoleccion}${g.coincide.sublote})` : ''}${g.coincide.analisis ? ` · ${g.coincide.analisis}` : ''}: same gold and no Big Bags in the app yet`,
                    )}
                    <NotaDeAyuda texto={A.ligar} />
                  </label>
                )}
                {!listo && f.incluir && !ligando && (
                  <div className="mt-2 grid gap-2 sm:grid-cols-3 lg:grid-cols-5">
                    {!g.soloAnalisis && (
                      <>
                        <label className="text-xs text-apagado">
                          {tr('No. C.O.', 'C.O. No.')}
                          <NotaDeAyuda texto={A.numeroCo} dentroDeEtiqueta />
                          <input
                            type="number"
                            min={1}
                            value={f.numero}
                            onChange={e => cambiar(g, {numero: e.target.value})}
                            className={campo}
                            aria-label={tr(`No. C.O. de ${nombre}`, `C.O. No. of ${nombre}`)}
                          />
                        </label>
                        <label className="text-xs text-apagado">
                          {tr('Fecha del C.O.', 'C.O. date')}
                          <NotaDeAyuda texto={A.fechaCo} dentroDeEtiqueta />
                          <input
                            type="date"
                            value={f.fecha}
                            onChange={e => cambiar(g, {fecha: e.target.value})}
                            className={campo}
                            aria-label={tr(`Fecha del C.O. de ${nombre}`, `C.O. date of ${nombre}`)}
                          />
                        </label>
                        <label className="text-xs text-apagado">
                          {tr('Comercialización', 'Commercialization')}
                          <NotaDeAyuda texto={A.fechaComercializacion} dentroDeEtiqueta />
                          <input
                            type="date"
                            value={f.comercializado}
                            onChange={e => cambiar(g, {comercializado: e.target.value})}
                            className={campo}
                            aria-label={tr(
                              `Fecha de comercialización de ${nombre}`,
                              `Commercialization date of ${nombre}`,
                            )}
                          />
                        </label>
                      </>
                    )}
                    <label className="text-xs text-apagado">
                      {tr('No. del análisis', 'Analysis No.')}
                      <NotaDeAyuda texto={A.numeroAnalisis} dentroDeEtiqueta />
                      <input
                        value={f.analisis}
                        onChange={e => cambiar(g, {analisis: e.target.value})}
                        className={campo}
                        aria-label={tr(`Análisis de ${nombre}`, `Analysis of ${nombre}`)}
                      />
                    </label>
                    {CAMPOS_DE_FECHAS.map(([clave, etiqueta]) => (
                      <label key={clave} className="text-xs text-apagado">
                        {trx(etiqueta)}
                        {clave === 'ensayoHasta' && <NotaDeAyuda texto={A.fechasAnalisis} dentroDeEtiqueta />}
                        <input
                          type="date"
                          value={f[clave]}
                          onChange={e => cambiar(g, {[clave]: e.target.value})}
                          className={campo}
                          aria-label={`${trx(etiqueta)} ${nombre}`}
                        />
                      </label>
                    ))}
                  </div>
                )}
                {problema && <p className="mt-1 text-xs text-alerta">{problema}</p>}
              </div>
            );
          })}
          {p.avisos.length > 0 && (
            <ul className="list-disc pl-5 text-xs text-dorado">
              {p.avisos.map(a => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          )}
          {error && <p className="text-alerta">{error}</p>}
          <button
            type="button"
            className={boton}
            disabled={!elegidos.length || incompleto || crear.isPending}
            onClick={confirmar}
          >
            {crear.isPending
              ? tr('Creando…', 'Creating…')
              : tr(
                  `Crear los certificados de ${elegidos.length} grupos`,
                  `Create the certificates of ${elegidos.length} groups`,
                )}
          </button>
        </div>
      )}
      {resultado && (
        <div className="mt-3 rounded-md bg-papel p-3" role="status" data-prueba="certificados-creados">
          <p className="font-medium text-verde-oscuro">{resultado.mensaje}</p>
          <ul className="mt-1 space-y-0.5 text-xs">
            {resultado.creados.map(c => (
              <li key={`${c.origenId}-${c.analisisId}`}>
                {c.titulo ?? c.exportador}
                {c.numero !== null ? ` · C.O. ${c.numero}` : ''}
                {c.analisis ? ` · ${c.analisis}` : ''} · {numero(c.bigBags)} BB
                {c.origenId !== null && (
                  <>
                    {' · '}
                    <a href={rutaDeDocumentoDeOrigen(c.origenId, 'origen')} className="text-verde hover:underline">
                      ⬇ C.O.
                    </a>
                    {' · '}
                    <a href={rutaDeDocumentoDeOrigen(c.origenId, 'planta')} className="text-verde hover:underline">
                      C.P.B.
                    </a>
                  </>
                )}
                {c.analisisId !== null && (
                  <>
                    {' · '}
                    <a href={rutaDeDocumentoDeAnalisis(c.analisisId)} className="text-verde hover:underline">
                      {tr('Análisis (Word)', 'Analysis (Word)')}
                    </a>
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
